import { type Map as MaplibreMap } from "maplibre-gl";

import { Timezone } from "@/constants/dateTime";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetTimeseriesData,
  TimeseriesType
} from "@/types/dataset";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const orderDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export const handleFilterFeaturesGroupByAttribute = (
  map: MaplibreMap | null,
  name: string,
  value: string,
  layerId: string
) => {
  if (!map || !value) return;

  const style = map.getStyle();

  if (!style) return;

  const allLayerIds = style.layers;

  const matchLayerId = allLayerIds.find((layer) =>
    layer.id.startsWith(layerId)
  );

  if (!matchLayerId) return;
  const isNumber = value !== "" && !Number.isNaN(Number(value));
  const parsedValue = isNumber ? Number(value) : value;

  map.setFilter(matchLayerId.id, ["==", ["get", name], parsedValue]);
};

export const getTypeTimeSeries = (
  categories: DatasetAttributeSchema[]
): TimeseriesType => {
  const groupByCategories = categories.find((cat) => cat.group_by_attr);

  if (!groupByCategories) return TimeseriesType.TIME; // default to time if no groupByCategories found

  const category = groupByCategories;

  if (category?.data_type === DataSchemaType.TIME) {
    return TimeseriesType.TIME;
  } else if (category?.data_type === DataSchemaType.TEXT) {
    return TimeseriesType.DAY;
  } else if (category?.data_type === DataSchemaType.INT) {
    return TimeseriesType.WEEK;
  } else if (category?.data_type === DataSchemaType.DATE) {
    return TimeseriesType.DATE;
  } else {
    return TimeseriesType.TIME; // default to time if type is unknown
  }
};

export const sortTimeSeriesData = (
  data: DatasetTimeseriesData[],
  type: TimeseriesType,
  timezone?: Timezone
): DatasetTimeseriesData[] => {
  if (type === TimeseriesType.DAY) {
    return [...data].sort(
      (a, b) => orderDays.indexOf(a.time) - orderDays.indexOf(b.time)
    );
  } else if (type === TimeseriesType.WEEK) {
    return [...data].sort((a, b) => Number(a.time) - Number(b.time));
  } else if (type === TimeseriesType.DATE) {
    return [...data].sort(
      (a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf()
    );
  } else {
    return [...data].sort((a, b) => {
      const hourA = dayjs.utc(a.time).tz(timezone?.code).hour();
      const hourB = dayjs.utc(b.time).tz(timezone?.code).hour();

      return hourA - hourB;
    });
  }
};
